<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { BellIcon, CheckCheckIcon, CheckIcon, EllipsisIcon, EyeOffIcon, LaptopIcon, Repeat2Icon } from '@lucide/vue';
import { formatDateTime, pluralize, type NotificationCategory, type NotificationItem } from '@yana/shared';
import { useNotifications } from '@/composables/useNotifications';
import { useAuthStore } from '@/stores/auth';
import { useToastStore } from '@/stores/toast';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ListPagination } from '@/components/ui/pagination';
import { Spinner } from '@/components/ui/spinner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const auth = useAuthStore();
const router = useRouter();
const toasts = useToastStore();
const {
  items, total, filters, loading, error, page, pages, from, to, hasPrev, hasNext,
  prev, next, refresh, unreadCount, canViewLogins, preferences, open, pending, read, readAll, hideCategory,
} = useNotifications();
const canOpenSettings = computed(() => auth.can('settings.manage'));
const showRotation = computed(() => preferences.value.rotationEnabled);
const showLogins = computed(() => canViewLogins.value && preferences.value.newDeviceEnabled);
const allHidden = computed(() => !showRotation.value && !showLogins.value);
const bellLabel = computed(() => unreadCount.value
  ? `Уведомления: ${pluralize(unreadCount.value, 'непрочитанное', 'непрочитанных', 'непрочитанных')}`
  : 'Уведомления');

function selectCategory(value: string | number): void {
  filters.value = { ...filters.value, category: String(value) as NotificationCategory };
}

async function openItem(item: NotificationItem): Promise<void> {
  const userId = auth.user?.id;
  if (!await read(item) || userId !== auth.user?.id) return;
  if (item.type === 'rotation') {
    open.value = false;
    if (!item.rotationRowId) {
      toasts.info('Строка перевахтовки удалена или недоступна');
      return;
    }
    await router.push({
      name: 'sheet', params: { code: 'rotation' },
      // Новый ключ повторяет переход и подсветку при клике по той же строке.
      query: { row: item.rotationRowId, reveal: crypto.randomUUID() },
    });
  }
}

function shortTime(value: string): string {
  const date = new Date(value);
  const today = new Date();
  return date.toLocaleDateString('ru-RU') === today.toLocaleDateString('ru-RU')
    ? date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button variant="ghost" size="icon-sm" class="relative" :aria-label="bellLabel" data-tip="Уведомления" data-mascot-bell>
        <BellIcon />
        <span v-if="unreadCount" class="absolute right-0 top-0 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-0.5 text-[9px] font-semibold tabular-nums text-primary-foreground" aria-hidden="true">
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </span>
      </Button>
    </PopoverTrigger>
    <PopoverContent align="end" :side-offset="8" :collision-padding="8" class="z-[60] flex w-[400px] max-w-[calc(100vw-16px)] flex-col overflow-hidden p-0" aria-label="Уведомления">
      <div class="flex h-11 shrink-0 items-center gap-2 px-3">
        <h2 class="text-[13px] font-semibold">Уведомления</h2>
        <span v-if="unreadCount" class="text-[11px] tabular-nums text-muted-foreground">{{ pluralize(unreadCount, 'новое', 'новых', 'новых') }}</span>
        <div class="ml-auto flex items-center gap-0.5">
          <Button variant="ghost" size="icon-sm" :disabled="!unreadCount || pending" aria-label="Отметить все прочитанными" data-tip="Все прочитаны" @click="readAll">
            <CheckCheckIcon class="size-4" />
          </Button>
          <DropdownMenu v-if="canOpenSettings">
            <DropdownMenuTrigger as-child>
              <Button variant="ghost" size="icon-sm" aria-label="Действия с вкладками уведомлений"><EllipsisIcon class="size-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="z-[70] w-64">
              <DropdownMenuItem v-if="canViewLogins && showRotation" :disabled="pending" @select="hideCategory('rotation')">
                <EyeOffIcon />Скрыть вкладку «Перевахтовка»
              </DropdownMenuItem>
              <DropdownMenuItem v-if="showLogins" :disabled="pending" @select="hideCategory('new_device')">
                <EyeOffIcon />Скрыть вкладку «Входы»
              </DropdownMenuItem>
              <DropdownMenuSeparator v-if="canViewLogins && !allHidden" />
              <DropdownMenuItem as-child>
                <RouterLink to="/settings/notifications" @click="open = false">Настроить уведомления…</RouterLink>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div class="flex items-center gap-1 border-b border-border px-3 pb-2">
        <Tabs :model-value="filters.category" class="min-w-0" @update:model-value="selectCategory">
          <TabsList class="h-7 gap-0 rounded-md p-0.5" aria-label="Категория уведомлений">
            <TabsTrigger value="all" class="h-6 px-2 text-[11px]">Все</TabsTrigger>
            <TabsTrigger v-if="showRotation" value="rotation" class="h-6 px-2 text-[11px]">Перевахтовка</TabsTrigger>
            <TabsTrigger v-if="showLogins" value="new_device" class="h-6 px-2 text-[11px]">Входы</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button variant="ghost" size="xs" class="ml-auto shrink-0 px-1.5 text-[11px]" :class="filters.unreadOnly ? 'bg-accent text-foreground' : 'text-muted-foreground'" :aria-pressed="filters.unreadOnly" @click="filters = { ...filters, unreadOnly: !filters.unreadOnly }">Новые</Button>
      </div>

      <div class="max-h-[min(420px,60dvh)] overflow-y-auto" :aria-busy="loading">
        <div v-if="error" class="px-4 py-6 text-center text-[12px]" role="alert">
          <p class="text-muted-foreground">{{ error }}</p>
          <Button variant="ghost" size="sm" class="mt-2 text-[12px]" @click="refresh">Повторить</Button>
        </div>
        <div v-else-if="loading && !items.length" class="flex items-center justify-center gap-2 py-8 text-[12px] text-muted-foreground"><Spinner class="size-4" />Загружаем…</div>
        <div v-else-if="!items.length" class="px-4 py-7 text-center">
          <p class="text-[12px] font-medium">{{ allHidden ? 'Вкладки скрыты' : filters.unreadOnly ? 'Всё прочитано' : 'Пока нет уведомлений' }}</p>
          <p class="mt-1 text-[11px] leading-4 text-muted-foreground">{{ allHidden ? 'Верните нужные вкладки в общих настройках.' : 'Новые события появятся здесь.' }}</p>
          <Button v-if="allHidden && canOpenSettings" as-child variant="link" size="sm" class="mt-1 text-[11px]"><RouterLink to="/settings/notifications" @click="open = false">Настроить вкладки</RouterLink></Button>
        </div>
        <ul v-else class="divide-y divide-border/70" aria-label="Список уведомлений">
          <li v-for="item in items" :key="item.id" class="group flex items-start gap-0.5 pr-1.5" :class="!item.readAt ? 'bg-primary/[0.035]' : ''">
            <Button variant="ghost" class="h-auto min-w-0 flex-1 shrink items-start justify-start gap-2 rounded-none px-3 py-2.5 text-left whitespace-normal" :aria-label="`${item.title}. ${item.body}${item.type === 'rotation' ? '. Показать строку в перевахтовке' : ''}`" @click="openItem(item)">
              <span class="relative mt-0.5 shrink-0">
                <Repeat2Icon v-if="item.type === 'rotation'" class="size-3.5 text-primary" />
                <LaptopIcon v-else class="size-3.5 text-muted-foreground" />
                <span v-if="!item.readAt" class="absolute -right-1 -top-1 size-1.5 rounded-full bg-primary" aria-label="Не прочитано" />
              </span>
              <span class="min-w-0 flex-1">
                <span class="flex min-w-0 items-baseline gap-2">
                  <span class="min-w-0 flex-1 truncate text-[12px] leading-4" :class="!item.readAt ? 'font-semibold' : 'font-medium'">{{ item.title }}</span>
                  <time :datetime="item.createdAt" :title="formatDateTime(item.createdAt)" class="shrink-0 text-[10px] font-normal tabular-nums text-muted-foreground">{{ shortTime(item.createdAt) }}</time>
                </span>
                <span class="mt-0.5 block line-clamp-2 text-[11px] font-normal leading-4 text-muted-foreground">{{ item.body }}</span>
              </span>
            </Button>
            <Button v-if="!item.readAt" variant="ghost" size="icon-xs" class="mt-2 shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100 focus-visible:opacity-100" :disabled="pending" :aria-label="`Прочитано: ${item.title}`" data-tip="Прочитано" @click="read(item)"><CheckIcon class="size-3.5" /></Button>
            <span v-else class="w-6 shrink-0" aria-hidden="true" />
          </li>
        </ul>
      </div>
      <ListPagination v-if="total > 0" :page="page" :pages="pages" :from="from" :to="to" :total="total" :has-prev="hasPrev" :has-next="hasNext" :loading="loading" :noun="['уведомления', 'уведомлений', 'уведомлений']" @prev="prev" @next="next" />
    </PopoverContent>
  </Popover>
</template>
